import React, { useContext, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { RootStateOrAny, useSelector } from "react-redux";
import { performancePages, BRAND_HEALTH_INDEX_CATEGORY } from "../../../../constants";
import { ThemeContext } from "../../../../context";
import { getBrandHealthIndexAverage, getBrandHealthIndexTrend } from "../../../../api/brandPerformanceApi";
import { withRouter, RouteComponentProps } from "react-router-dom";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import { isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const BrandHealthIndexTooltip = () => (
  <>
    <p>
      Brand Health Index average calculated by taking average of six components (Impression, Quality, Recommend, Reputation, Satisfaction, Value).
    </p>
    <p>
      <u>Source</u>: YouGov
    </p>
    <br />
  </>
);

const BrandHealthIndex = withRouter((props: Props) => {
  const { theme } = useContext(ThemeContext);
  const { onHandleRadioClick, nissanCompetitor, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const model = params.model;
  const category = params.category;
  const isIndexCategory = category?.toLowerCase() === BRAND_HEALTH_INDEX_CATEGORY;

  const {
    data: chartData,
    isFetching: loadingTrend,
    refetch: refetchTrend,
  } = useQuery(
    ["brandHealthIndexTrend", granularity, history.location.search],
    () => getBrandHealthIndexTrend({ ...params, nissan_competitor: nissanCompetitor }),
    { enabled: false }
  );

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchAverage,
  } = useQuery(
    ["brandHealthIndexAverage", granularity, history.location.search],
    () => getBrandHealthIndexAverage({ ...params, nissan_competitor: nissanCompetitor }),
    { enabled: false }
  );

  useEffect(() => {
    refetchAverage();
    refetchTrend();
  }, [history.location.search]);

  const columns = [
    {
      id: "model",
      Header: `${model == "Brand" ? "Brand" : "Model"} `,
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => props1.value,
    },
    {
      Header: "Brand Health Index",
      accessor: "percentage_average_score",
      id: "percentage_average_score",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const value = props1.cell.row.original?.["percentage_average_score"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
    {
      Header: "YoY",
      accessor: "yoy",
      id: "yoy",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const value = props1.cell.row.original?.["yoy"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
  ];

  const dateRange = tableData?.date_range ?? [];
  const dateTitle = useMemo(() => {
    if (dateRange && dateRange.length > 1) return `(${dateRange[0]} - ${dateRange.slice(-1)})`;
    return "";
  }, [dateRange]);

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title={category ?? "Brand Health Index Trend"}
        chartId="brandHealthIndexTrend"
        chartData={chartData}
        loadingTrend={loadingTrend}
        diff={diff}
        TrendTooltipParagraph={isIndexCategory ? BrandHealthIndexTooltip : undefined}
        nissanCompetitor={nissanCompetitor}
        onHandleRadioClick={onHandleRadioClick}
        chartGranularity={granularity}
        isPercentage={true}
      />
      <BrandPerformanceTableTile
        title={`${category ?? "Brand Health Index"} Average`}
        tableId="brand_health_index_table"
        tableData={tableData}
        columns={columns}
        loadingTable={loadingTable}
        initialSortCol={{ id: "percentage_average_score", desc: true }}
        TableTooltipParagraphs={isIndexCategory ? BrandHealthIndexTooltip : undefined}
      />
    </div>
  );
});

export default BrandHealthIndex;
